import { zodResolver } from "@hookform/resolvers/zod";
import React, { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Button from "../components/inputs/Button";
import Dropdown from "../components/inputs/Dropdown";
import Input from "../components/inputs/Input";
import ProgressBar from "../components/shared/ProgressBar";
import Textarea from "../components/shared/Textarea";
import { useAuth } from "../hooks/useAuth";
import { cadreAPI } from "../services/api";
import { cadreProfileUpdateSchema } from "../types/schemas";

const CadreProfile = () => {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [originalData, setOriginalData] = useState({});
  const [autoSaveMessage, setAutoSaveMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(cadreProfileUpdateSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      organization: "",
      position: "",
      yearsExperience: "",
      expertise: [],
      region: "",
      availability: "Available",
      cv: null,
      coverLetter: "",
    },
    mode: "onChange",
  });

  const watchedData = watch();

  const expertiseOptions = [
    "Coastal Engineering",
    "Hydraulic Engineering",
    "Geotechnical Engineering",
    "Alluvial Fan Engineering",
    "Levee Design and Mapping",
    "1D/2D Floodway Analysis",
    "Storm Surge Modeling",
    "Sediment Transport",
    "Dam Safety",
    "Floodplain Management",
    "FEMA Guidelines",
    "Risk Assessment",
  ];

  const regionOptions = [
    "Northeast",
    "Southeast",
    "Midwest",
    "Southwest",
    "West Coast",
    "Gulf Coast",
    "Great Lakes",
    "Pacific Northwest",
  ];

  const availabilityOptions = [
    { value: "Available", label: "Available for all panels" },
    { value: "Limited", label: "Limited availability" },
    { value: "Not Available", label: "Currently unavailable" },
  ];

  const fetchProfile = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const response = await cadreAPI.getMyCadreProfile();

      if (response.success && response.profile) {
        const profile = {
          firstName: user.name?.split(" ")[0] || "",
          lastName: user.name?.split(" ").slice(1).join(" ") || "",
          email: response.profile.User?.email || user.email || "",
          phone: response.profile.User?.phone || "",
          organization: response.profile.organization || "",
          position: response.profile.position || "",
          yearsExperience: response.profile.yearsExperience || "",
          expertise: response.profile.expertise
            ? response.profile.expertise.split(",")
            : [],
          region: response.profile.region || "",
          availability: response.profile.availability || "Available",
          coverLetter: response.profile.bio || "",
        };
        reset(profile);
        setOriginalData(profile);
      } else {
        // Initialize with user data if no profile exists
        const initialProfile = {
          firstName: user.name?.split(" ")[0] || "",
          lastName: user.name?.split(" ").slice(1).join(" ") || "",
          email: user.email || "",
          phone: user.phone || "",
          organization: "",
          position: "",
          yearsExperience: "",
          expertise: [],
          region: "",
          availability: "Available",
          coverLetter: "",
        };
        reset(initialProfile);
        setOriginalData(initialProfile);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      setError("Failed to load profile data");
      toast.error("Failed to load profile data");
    } finally {
      setLoading(false);
    }
  }, [user, reset]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  useEffect(() => {
    // Auto-save functionality
    if (isEditing && isDirty) {
      const autoSaveTimer = setTimeout(() => {
        handleSubmit((data) => saveProfile(data, true))();
      }, 3000);

      return () => clearTimeout(autoSaveTimer);
    }
    // saveProfile is stable enough for debounce; include handleSubmit for eslint
    // eslint-disable-next-line react-hooks/exhaustive-deps -- debounce on form dirtiness only
  }, [watchedData, isEditing, isDirty, handleSubmit]);

  const saveProfile = async (data, isAutoSave = false) => {
    try {
      const updateData = {
        ...data,
        expertise: Array.isArray(data.expertise)
          ? data.expertise.join(",")
          : data.expertise,
      };

      const response = await cadreAPI.updateCadreProfile(updateData);

      if (response.success) {
        setOriginalData(data);

        if (isAutoSave) {
          setAutoSaveMessage("Auto-saved");
          setTimeout(() => setAutoSaveMessage(""), 2000);
        } else {
          toast.success("Profile updated successfully!");
          setIsEditing(false);
        }
      } else {
        throw new Error(response.message || "Failed to save profile");
      }
    } catch (error) {
      console.error("Error saving profile:", error);
      if (!isAutoSave) {
        toast.error(
          "Failed to save profile: " + (error.message || "Unknown error")
        );
      }
    }
  };

  const onSubmit = (data) => {
    saveProfile(data, false);
  };

  const cancelEdit = () => {
    reset(originalData);
    setIsEditing(false);
  };

  const getCompletionPercentage = () => {
    const fields = [
      watchedData.firstName,
      watchedData.lastName,
      watchedData.email,
      watchedData.phone,
      watchedData.organization,
      watchedData.position,
      watchedData.yearsExperience,
      watchedData.expertise?.length > 0,
      watchedData.region,
      watchedData.coverLetter,
    ];

    const completed = fields.filter((field) => field).length;
    return Math.round((completed / fields.length) * 100);
  };

  const completionPercentage = getCompletionPercentage();

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center">
        <div className="text-lg text-gray-600">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-200px)] bg-gradient-to-br from-gray-50 via-blue-50 to-gray-100">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
            <p className="text-gray-600 mt-2">
              Manage your Cadre Member profile and expertise
            </p>
          </div>
          <div className="flex items-center space-x-4">
            {autoSaveMessage && (
              <span className="text-sm text-green-600 animate-fadeIn">
                {autoSaveMessage}
              </span>
            )}
            {!isEditing ? (
              <Button variant="primary" onClick={() => setIsEditing(true)}>
                Edit Profile
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={cancelEdit}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  className="bg-emerald-800 hover:bg-emerald-900"
                  onClick={handleSubmit(onSubmit)}
                >
                  Save Changes
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Profile Completion */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex justify-between items-center mb-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Profile Completion
            </h3>
            <span className="text-2xl font-bold text-custom-blue">
              {completionPercentage}%
            </span>
          </div>
          <ProgressBar
            value={completionPercentage}
            max={100}
            showLabel={false}
            variant="primary"
          />
          <p className="text-sm text-gray-600 mt-2">
            {completionPercentage < 100
              ? "Complete your profile to increase your chances of being selected for panels"
              : "Great! Your profile is complete"}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Main Profile Information */}
            <div className="lg:col-span-2 space-y-6">
              {/* Personal Information */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Personal Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="First Name"
                        type="text"
                        name="firstName"
                        field={field}
                        error={errors.firstName}
                        disabled={!isEditing}
                        required
                      />
                    )}
                  />
                  <Controller
                    name="lastName"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="Last Name"
                        type="text"
                        name="lastName"
                        field={field}
                        error={errors.lastName}
                        disabled={!isEditing}
                        required
                      />
                    )}
                  />
                  <Controller
                    name="email"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="Email"
                        type="email"
                        name="email"
                        field={field}
                        error={errors.email}
                        disabled={!isEditing}
                        required
                      />
                    )}
                  />
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="Phone"
                        type="tel"
                        name="phone"
                        field={field}
                        error={errors.phone}
                        placeholder="+1234567890"
                        disabled={!isEditing}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Professional Information */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Professional Information
                </h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Controller
                      name="organization"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="Organization"
                          type="text"
                          name="organization"
                          field={field}
                          error={errors.organization}
                          disabled={!isEditing}
                          required
                        />
                      )}
                    />
                    <Controller
                      name="position"
                      control={control}
                      render={({ field }) => (
                        <Input
                          label="Position/Title"
                          type="text"
                          name="position"
                          field={field}
                          error={errors.position}
                          disabled={!isEditing}
                          required
                        />
                      )}
                    />
                  </div>

                  <Controller
                    name="yearsExperience"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label="Years of Experience"
                        type="number"
                        name="yearsExperience"
                        field={field}
                        error={errors.yearsExperience}
                        disabled={!isEditing}
                        required
                      />
                    )}
                  />

                  <Controller
                    name="coverLetter"
                    control={control}
                    render={({ field }) => (
                      <Textarea
                        label="Professional Bio / Cover Letter"
                        name="coverLetter"
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.coverLetter?.message}
                        disabled={!isEditing}
                        rows={4}
                        placeholder="Describe your professional background and experience..."
                        maxLength={2000}
                        showCount
                      />
                    )}
                  />
                </div>
              </div>

              {/* Expertise Areas */}
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Areas of Expertise
                </h2>
                {errors.expertise && (
                  <p className="text-sm text-red-500 mb-2">
                    {errors.expertise.message}
                  </p>
                )}
                <Controller
                  name="expertise"
                  control={control}
                  render={({ field }) => (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {expertiseOptions.map((expertise) => (
                        <label
                          key={expertise}
                          className={`flex items-center p-2 rounded-lg ${
                            isEditing
                              ? "hover:bg-gray-50 cursor-pointer"
                              : "cursor-not-allowed"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={field.value?.includes(expertise)}
                            onChange={(e) => {
                              if (!isEditing) return;
                              const newValue = e.target.checked
                                ? [...(field.value || []), expertise]
                                : (field.value || []).filter(
                                    (e) => e !== expertise
                                  );
                              field.onChange(newValue);
                            }}
                            disabled={!isEditing}
                            className="mr-2"
                          />
                          <span
                            className={`text-sm ${
                              field.value?.includes(expertise)
                                ? "text-gray-900 font-medium"
                                : "text-gray-600"
                            }`}
                          >
                            {expertise}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Availability */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Availability
                </h3>
                <div className="space-y-3">
                  <Controller
                    name="availability"
                    control={control}
                    render={({ field }) => (
                      <Dropdown
                        headLabel="Status"
                        label="Select availability"
                        options={availabilityOptions}
                        value={field.value}
                        onSelect={field.onChange}
                        error={errors.availability}
                        disabled={!isEditing}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Region */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Preferred Region
                </h3>
                {errors.region && (
                  <p className="text-sm text-red-500 mb-2">
                    {errors.region.message}
                  </p>
                )}
                <Controller
                  name="region"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      headLabel="Region"
                      label="Select region"
                      options={regionOptions.map((r) => ({
                        value: r,
                        label: r,
                      }))}
                      value={field.value}
                      onSelect={field.onChange}
                      error={errors.region}
                      disabled={!isEditing}
                      required
                    />
                  )}
                />
              </div>

              {/* Quick Stats */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Profile Statistics
                </h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">
                      Panels Participated
                    </span>
                    <span className="font-semibold text-gray-900">3</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Active Panels</span>
                    <span className="font-semibold text-gray-900">1</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">Member Since</span>
                    <span className="font-semibold text-gray-900">2024</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm text-gray-600">
                      Application Status
                    </span>
                    <span className="font-semibold text-green-600">
                      Approved
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CadreProfile;
